/**
 * X との相性（docs/X-AFFINITY.md）の SDK 側。サーバーは apps/server/src/modules/share（+ players/oauth/xProfile.ts・players/transfer.ts）。
 *
 *   kumo.share({ kind, leaderboard?, score?, text? … })   X の投稿画面（Web intent）を開く。共有 ID（?ks=）を発行してゲームの URL に付ける
 *   kumo.share.intentUrl({ text, url, hashtags, via })     投稿画面の URL だけ作る（API も鍵も使わない）
 *   kumo.share.incoming()                                   共有のリンクから開かれたなら、その共有（挑戦状のスコアなど）。無ければ null
 *   kumo.share.inAppBrowser()                               'x' = X のアプリ内ブラウザで開かれている
 *   kumo.share.openInBrowser()                              いまのゲストのまま普段のブラウザで開くリンクを作る（アプリ内ブラウザ対策）
 *   kumo.x.profile() / setProfileVisible(v) / profiles(ids) / withProfiles(entries)   X でログインした人の名前とアイコン（本人が公開を選んだ人だけ）
 *
 * どれもクリエイターが config で ON にしたときだけ動く（既定 OFF）。OFF のときの挙動:
 *   share()   … 共有 ID なしで投稿画面を開く（ゲームの「シェア」ボタンは壊れない）
 *   incoming  … null
 *   記録      … 何もしない（サーバーの 404 を黙って受け流す）
 *
 * X の Web intent（一次資料 docs.x.com「Web intent」2026-09-26 確認）: `https://x.com/intent/tweet?text=&url=&hashtags=&via=`。
 * API キーも費用も要らない（Kumo の負担ゼロ）。X-AFFINITY.md の `intent/post` は一次資料に無かったので、文書化されている `intent/tweet` を使う。
 * url は X が t.co で短縮する。text + url + hashtags + via で 280 文字以内（X の規則）。
 */

import { KumoError, type KumoCore, type KumoPlayer, type KumoSession } from './core.js';

export const INTENT_URL = 'https://x.com/intent/tweet';

/** 共有 ID の形（サーバーの SHARE_ID_RE と同じ） */
const SHARE_ID_RE = /^[A-Za-z0-9]{10}$/;
/** URL のクエリ名。短い = 投稿の文字数を食わない。ゲーム自身のクエリとぶつかりにくい名前にした */
export const SHARE_PARAM = 'ks';

export interface IntentOptions {
  text?: string;
  url?: string;
  hashtags?: string[];
  /** X のハンドル（@ なし）。投稿の末尾に「via @…」 */
  via?: string;
}

export function intentUrl(o: IntentOptions): string {
  const u = new URL(INTENT_URL);
  if (o.text) u.searchParams.set('text', o.text);
  if (o.url) u.searchParams.set('url', o.url);
  if (o.hashtags?.length) u.searchParams.set('hashtags', o.hashtags.map((h) => h.replace(/^#/, '')).join(','));
  if (o.via) u.searchParams.set('via', o.via.replace(/^@/, ''));
  return u.toString();
}

export interface ShareOptions {
  /** plain（既定・記録だけ）/ score / rank / challenge（「このスコアを超えてみろ」） */
  kind?: 'plain' | 'score' | 'rank' | 'challenge';
  /** ランキングのキー。指定すると値と順位はサーバーがランキングから読む（改造されたゲームから偽のスコアで挑戦状を作れない） */
  leaderboard?: string;
  /** ランキングを使わないゲームのスコア（受け取った側には verified: false と分かる） */
  score?: number;
  /** カードと挑戦状に自分の表示名を出すか（既定 false） */
  showName?: boolean;
  /** 投稿文。省略時は config の share.links.text（{score} {rank} が置き換わる） */
  text?: string;
  hashtags?: string[];
  via?: string;
  /** 共有する URL。既定は今のページ（fragment を除く）。?ks=<共有 ID> が付く */
  url?: string;
  /** false = 投稿画面を開かず URL だけ返す（ゲームが自分でリンクを出す） */
  open?: boolean;
}

export interface SharedLink {
  id: string;
  kind: 'plain' | 'score' | 'rank' | 'challenge';
  score: number | null;
  rank: number | null;
  /** true = サーバーがランキングから読んだ値 */
  verified: boolean;
  leaderboard: { key: string; title: string | Record<string, string> | null; order: 'asc' | 'desc' | null; format: string | null } | null;
  displayName: string | null;
  createdAt: string;
}

export interface ShareResult {
  /** 共有 ID（share.links が OFF なら null） */
  shareId: string | null;
  /** 投稿に載る URL（?ks= 付き） */
  url: string;
  /** X の投稿画面の URL */
  intentUrl: string;
  /**
   * 投稿画面を開けたか（ポップアップが止められた・open: false・X のアプリ内ブラウザなら false = ゲームが intentUrl をリンクとして出すか、
   * 投稿文と url をコピーさせる）
   */
  opened: boolean;
  /** 'x' = X のアプリ内ブラウザだったので窓を開かなかった（ゲームは「コピー」や案内を出す） */
  inAppBrowser: 'x' | null;
  /** 描いたカード画像（share.images が ON のとき） */
  imageUrl: string | null;
  link: SharedLink | null;
}

/** SDK が触るブラウザの部分（テストで差し替える） */
export interface ShareBrowser {
  location: { href: string; hash: string; search: string };
  history?: { replaceState(data: unknown, unused: string, url?: string): void };
  open?(url: string, target: string, features?: string): { location: { href: string }; close(): void; opener?: unknown } | null;
  navigator?: { userAgent?: string };
}

function defaultBrowser(): ShareBrowser | null {
  const w = globalThis as unknown as { window?: ShareBrowser };
  return w.window && w.window.location ? w.window : null;
}

const stripHash = (href: string) => href.split('#')[0]!;

/** URL の ks を付け替える（既にあれば置き換え = 受け取った共有を再共有しても自分の共有 ID になる） */
export function withShareParam(href: string, shareId: string | null): string {
  const u = new URL(stripHash(href));
  u.searchParams.delete(SHARE_PARAM);
  if (shareId) u.searchParams.set(SHARE_PARAM, shareId);
  return u.toString();
}

/**
 * X のアプリ内ブラウザか。X の iOS / Android アプリのアプリ内ブラウザは User-Agent に「Twitter」を含む（"Twitter for iPhone"・"TwitterAndroid"）。
 * WHY UA で見るか: アプリ内ブラウザを判別する標準の API は無い。外れても害は小さい（ログインの既定がリダイレクトになる・案内が出ないだけ）。
 * 2026-09-26 時点で X の公式の資料は見つからず、既知の UA と開発者コミュニティの報告に基づく（README「要確認」）。
 * 2026-09 の実際の UA [二次: Roboin Blog 2026-09-19・Research の X 調査 §2]:
 *   iOS     … `… Mobile/24A437 Twitter for iPhone/12.26`（Safari/ と Version/ が無い = WKWebView 系の独自ブラウザ）
 *   Android … `… (Linux; Android 17; …; wv) … Mobile Safari/537.36 TwitterAndroid/12.28.0-alpha.01`（wv = Android System WebView）
 *   どちらも /Twitter/i に一致するので判定は変えない（iPad の UA は未確認）
 */
export function detectInAppBrowser(ua: string | undefined): 'x' | null {
  return ua && /Twitter/i.test(ua) ? 'x' : null;
}

/**
 * X のアプリ内ブラウザで出す既定の案内文（英語。対象は北米のクリエイター・X の利用者）。ゲームが自分の文言を出してもよい。
 * X のアプリは右上（iOS）/ 右上の ⋮ か …（Android）のメニューに「ブラウザで開く」がある（UI の位置は版で変わるので「メニュー」とだけ言う）
 */
export const IN_APP_HINT = 'Copy the link, or tap the ••• menu and choose "Open in browser". Your progress comes with you.';

/** 端末に覚える最初の流入元（first-touch）と、最後にプレイを記録した日（1 日 1 回にする） */
interface StoredShare {
  id: string;
  lastPlay: string;
}

/** UTC の日付（サーバーの share_daily の day と同じ = 「1 人 1 日 1 回」の日の区切りを端末とサーバーで揃える） */
const utcDay = (d: Date) => d.toISOString().slice(0, 10);

export interface CreateShareOptions {
  /**
   * 時計（既定 = 今の時刻）。テストで日付の境目（UTC 0:00）の前後を確かめるための口（T-13・2026-09-27 test-hardening）。
   * WHY 引数で渡すか: 実時計のままだと、テストの途中で UTC 0:00 をまたぐと「同じ日の 2 回目」が別の日になり送ってしまう。
   * 本番（index.ts）は渡さない = 今までと同じ動き
   */
  now?: () => Date;
}

export function createShare(core: KumoCore, browserOverride?: ShareBrowser | null, options: CreateShareOptions = {}) {
  const now = options.now ?? (() => new Date());
  const today = () => utcDay(now());
  const browser = () => (browserOverride === undefined ? defaultBrowser() : browserOverride);
  const storeKey = core.storageKey('share');
  let incoming: Promise<SharedLink | null> | null = null;

  const landedId = (): string | null => {
    const b = browser();
    if (!b) return null;
    try {
      const id = new URLSearchParams(b.location.search).get(SHARE_PARAM);
      return id && SHARE_ID_RE.test(id) ? id : null;
    } catch {
      return null;
    }
  };

  const readStored = (): StoredShare | null => {
    try {
      const v = JSON.parse(core.storage.getItem(storeKey) || 'null') as StoredShare | null;
      return v && SHARE_ID_RE.test(v.id) ? v : null;
    } catch {
      return null;
    }
  };

  /** 404（その道具が OFF・知らない共有 ID）は「使っていない」として黙る。それ以外もゲームは止めない */
  const quiet = <T>(p: Promise<T>): Promise<T | null> =>
    p.catch((e) => {
      if (!(e instanceof KumoError && e.status === 404)) console.warn('[game-backend] share:', e);
      return null;
    });

  const visit = (shareId: string, landed: boolean) =>
    quiet(core.request('POST', '/v1/share/visits', { shareId, landed }, { auth: core.isSignedIn }));

  /**
   * init が認証の後に 1 回呼ぶ。待たせない（ゲームの起動を遅らせない）: 呼ぶ側は await しない。
   *   ?ks= で来た … 挑戦状を読み込み（incoming）、訪問を記録。最初の流入元として端末に覚える
   *   覚えた共有がある … 今日まだならプレイを記録（1 日 1 回。サーバー側でも 1 人 1 日 1 回）
   */
  const start = () => {
    const id = landedId();
    const stored = readStored();
    // 日付は 1 回だけ読む（比較と保存の間に UTC 0:00 をまたいでも、同じ 1 回の起動の中では同じ日として扱う）
    const day = today();
    if (id) {
      incoming ??= quiet(core.request<{ link: SharedLink }>('GET', `/v1/share/links/${id}`, undefined, { auth: false })).then((r) => r?.link ?? null);
      const first = stored ?? { id, lastPlay: day };
      if (!stored || stored.lastPlay !== day) core.storage.setItem(storeKey, JSON.stringify({ ...first, lastPlay: day }));
      return visit(id, true);
    }
    if (stored && stored.lastPlay !== day) {
      core.storage.setItem(storeKey, JSON.stringify({ ...stored, lastPlay: day }));
      return visit(stored.id, false);
    }
    return Promise.resolve(null);
  };

  async function share(opts: ShareOptions = {}): Promise<ShareResult> {
    const b = browser();
    const inApp = detectInAppBrowser(b?.navigator?.userAgent);
    // ブラウザはユーザー操作の中で同期的に開いた窓しか許さないので、await の前に空の窓を開く（store.ts の決済・OAuth と同じ）。
    // X のアプリ内ブラウザでは開かない（2026-09-27 Research の X 調査 §2）: Android の WebView は window.open を「同じ WebView の遷移」に
    // するのが既定 = ゲームのページが投稿画面に置き換わり、遊んでいた状態が消える。iOS はそもそも開かない。
    // → opened: false で返し、ゲームが intentUrl のリンクかコピーを出す（既存のポップアップが止められた時と同じ代替）
    const popup = opts.open === false || inApp ? null : (b?.open?.('', '_blank') ?? null);
    // 空の窓は 'noopener' を付けて開けない（付けると null が返り、後から X の URL を入れられない）。代わりに opener を切る:
    // X のページから window.opener でゲームのタブを書き換えられないように（reverse tabnabbing 対策・first10 2026-09-27）
    if (popup) {
      try {
        popup.opener = null;
      } catch {
        // 読み取り専用の環境でも共有は続ける
      }
    }
    type Created = { link: SharedLink; imageUrl: string | null; post: { text: string | null; hashtags: string[]; via: string | null } };
    // 共有 ID が作れない（OFF・未サインイン・通信の失敗）ときも投稿画面は開く = ゲームの「シェア」ボタンは壊れない
    const created: Created | null = core.isSignedIn
      ? await quiet(
          core.request<Created>('POST', '/v1/share/links', {
            kind: opts.kind ?? 'plain',
            ...(opts.leaderboard ? { leaderboard: opts.leaderboard } : {}),
            ...(opts.score !== undefined ? { score: opts.score } : {}),
            showName: !!opts.showName
          })
        )
      : null;
    const base = opts.url ?? (b ? b.location.href : '');
    const url = base ? withShareParam(base, created?.link.id ?? null) : '';
    const intent = intentUrl({
      text: opts.text ?? created?.post.text ?? undefined,
      url: url || undefined,
      hashtags: opts.hashtags ?? created?.post.hashtags,
      via: opts.via ?? created?.post.via ?? undefined
    });
    let opened = false;
    if (popup) {
      popup.location.href = intent;
      opened = true;
    }
    return { shareId: created?.link.id ?? null, url, intentUrl: intent, opened, inAppBrowser: inApp, imageUrl: created?.imageUrl ?? null, link: created?.link ?? null };
  }

  return Object.assign(share, {
    intentUrl,
    /** 共有のリンク（?ks=）から開かれたなら、その共有。挑戦状ならスコアを見せて「超えてみろ」の画面を出せる */
    incoming(): Promise<SharedLink | null> {
      return incoming ?? Promise.resolve(null);
    },
    inAppBrowser(): 'x' | null {
      return detectInAppBrowser(browser()?.navigator?.userAgent);
    },
    /**
     * いまのプレイヤーのまま、普段のブラウザで開くための URL（`#auth_transfer=<一回限り・10 分>`）を作る。
     * X のアプリ内ブラウザは保存領域が普段のブラウザと別なので、そのまま「ブラウザで開く」とゲストの進捗が移らない。
     * X / Google のサインインが signin_needs_browser を返したときもこれを使う（移った先でサインインする）。
     *
     * X のアプリ内ブラウザの中では window.open を呼ばない（2026-09-27 Research の X 調査 §2・変更前は呼んでいた）:
     *   Android の WebView は新しい窓を「同じ WebView の遷移」にするのが既定 → 引き継ぎコードがアプリ内ブラウザ自身で使われて消え
     *   （一回限り）、ゲームのページも置き換わる。しかも opened: true と誤って返っていた。iOS はそもそも開かない。
     *   → url と hint（「… → ブラウザで開く」の案内文）だけ返す。ゲームは「コピー」ボタンと hint を出す。
     * アプリ内ブラウザでなければ従来どおり新しい窓で開こうとする（opened が false でも url をコピーに使える）。
     * この URL は 10 分間このプレイヤーそのもの = 投稿や他人に渡さないよう画面で注意する
     */
    async openInBrowser(): Promise<{ url: string; opened: boolean; expiresIn: number; inAppBrowser: 'x' | null; hint: string | null }> {
      const b = browser();
      if (!b) throw new KumoError(0, 'no_browser', 'openInBrowser needs a browser window');
      const inApp = detectInAppBrowser(b.navigator?.userAgent);
      // コードは 10 分間このプレイヤーそのものなので、この端末のリフレッシュトークンを添える（SEC-ACCESS-02）
      const r = await core.withDeviceProof((proof) => core.request<{ code: string; expiresIn: number }>('POST', '/v1/auth/transfer', proof));
      // fragment の名前に Kumo を出さない（プレイヤーがコピーして貼る URL。PRIN-07）。読む側は旧名 kumo_transfer も受ける
      const url = `${stripHash(b.location.href)}#auth_transfer=${encodeURIComponent(r.code)}`;
      if (inApp) return { url, opened: false, expiresIn: r.expiresIn, inAppBrowser: inApp, hint: IN_APP_HINT };
      const w = b.open?.(url, '_blank', 'noopener') ?? null;
      return { url, opened: !!w, expiresIn: r.expiresIn, inAppBrowser: null, hint: null };
    },
    /** init が使う（ゲームは呼ばない） */
    _start: start
  });
}

/**
 * 起動時: URL の `#auth_transfer=`（旧名 `#kumo_transfer=` も）を換えて、同じプレイヤーのセッションにする（init が resume より先に呼ぶ）。
 * 換えたら true。fragment は結果に関わらず消す（再読み込み・再共有でコードを使い回さない）
 */
export async function redeemTransferFromUrl(core: KumoCore, browserOverride?: ShareBrowser | null): Promise<boolean> {
  const b = browserOverride === undefined ? defaultBrowser() : browserOverride;
  if (!b) return false;
  // コードの形（サーバーの transfer.ts: randomBytes(32).toString('base64url') = 43 文字の base64url）が合うときだけ扱う。
  // auth_transfer は開始の記録（別の端末から来る URL）で絞れないので、ゲーム自身の #auth_transfer=… を壊さないよう形で見分ける（2026-09-27 リーダー指示）
  const m = /(?:^#|&)(?:auth|kumo)_transfer=([A-Za-z0-9_-]{43})(?:&|$)/.exec(b.location.hash);
  if (!m) return false;
  b.history?.replaceState(null, '', stripHash(b.location.href));
  try {
    const s = await core.request<KumoSession>('POST', '/v1/auth/transfer/redeem', { code: decodeURIComponent(m[1]!) }, { auth: false });
    core.adoptSession(s);
    return true;
  } catch (e) {
    // 期限切れ・使用済み: この端末の前のセッション（あれば）で続ける
    console.warn('[game-backend] could not continue the session from the other browser', e);
    return false;
  }
}

export interface XProfile {
  username: string;
  name: string | null;
  avatarUrl: string | null;
}

/** GET /v1/players/x-profiles の 1 回に載せる id の数（サーバーの 1 回の上限 = 100）。多いときは分けて送る */
export const X_PROFILES_PER_REQUEST = 100;

/** kumo.x — X でログインした人の名前とアイコン（本人が公開を選んだときだけ他の人に見える） */
export function createX(core: KumoCore) {
  // 2026-10-04 creator/limits-clients（洗い出しの不具合）: 以前は .slice(0, 100) で 101 件目から黙って捨てていた（ランキングの 101 位から下に
  //   アイコンが出ない）。サーバーの口は 1 回 100 件まで（players/oauth/xProfile.ts）なので、100 件ずつに分けて並べて取り、1 つにまとめる。
  //   X_PROFILES_PER_REQUEST は 1 回に送る数の目安（サーバーの 1 回の上限）で、呼ぶ側への上限ではない
  const profiles = async (ids: string[]): Promise<Record<string, XProfile>> => {
    const unique = [...new Set(ids)];
    if (!unique.length) return {};
    const parts: string[][] = [];
    for (let i = 0; i < unique.length; i += X_PROFILES_PER_REQUEST) parts.push(unique.slice(i, i + X_PROFILES_PER_REQUEST));
    const results = await Promise.all(
      parts.map((p) => core.request<{ profiles: Record<string, XProfile> }>('GET', `/v1/players/x-profiles?ids=${encodeURIComponent(p.join(','))}`, undefined, { auth: false }))
    );
    return Object.assign({}, ...results.map((r) => r.profiles));
  };
  return {
    /** 自分の X プロフィール（X を連携していなければ profile: null）と公開の設定 */
    profile(): Promise<{ profile: XProfile | null; visible: boolean }> {
      return core.request('GET', '/v1/players/me/x-profile');
    },
    /** ランキング等に X の名前とアイコンを出すか（本人の同意。既定は出さない） */
    setProfileVisible(visible: boolean): Promise<{ profile: XProfile; visible: boolean }> {
      return core.request('PUT', '/v1/players/me/x-profile', { visible });
    },
    profiles,
    /** ランキングの entries に xProfile を付けて返す（公開していない人は null）。例: kumo.x.withProfiles((await kumo.leaderboards.get('high')).entries) */
    async withProfiles<T extends { playerId: string }>(entries: T[]): Promise<Array<T & { xProfile: XProfile | null }>> {
      const map = await profiles(entries.map((e) => e.playerId)).catch(() => ({}) as Record<string, XProfile>);
      return entries.map((e) => ({ ...e, xProfile: map[e.playerId] ?? null }));
    }
  };
}

export type { KumoPlayer };
